// src/pages/Dashboard.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell.jsx";
import DashboardMapView from "../components/DashboardMapView.jsx";
import DashboardMapRiskView from "../components/DashboardMapRiskView.jsx";
import FieldShapeIcon from "../components/FieldShapeIcon.jsx";
import { apiFetchJson, getStoredUser } from "../api/api.js";

function fmt(n) {
  if (n === null || n === undefined) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString("uk-UA");
}

function fmt1(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString("uk-UA", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function fmt2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function safeNum(n, d = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : d;
}

const MODES = { CROP: "CROP", RISK: "RISK" };

const TASK_PERIODS = [
  { value: 14, label: "14 днів" },
  { value: 30, label: "30 днів" },
  { value: 45, label: "45 днів" },
];

const CLIMA_SUBTYPE_TO_CAT = {
  FROST: "TEMPERATURE", HEAT: "TEMPERATURE",
  DROUGHT: "MOISTURE", HAIL: "MOISTURE",
  STRONG_WIND: "WIND", TORNADO: "WIND",
  SNOWFALL: "SNOW", SNOW_AVALANCHE: "SNOW", SPRING_THAW: "SNOW",
  FUNGAL_DISEASE: "DISEASES_PESTS", PEST_REPRODUCTION: "DISEASES_PESTS", WEEDS: "DISEASES_PESTS",
  INSUFFICIENT_LIGHT: "SOLAR_RADIATION", UV_RADIATION: "SOLAR_RADIATION",
  THUNDERSTORM: "ATMOSPHERE_PHENOMENA", FOG: "ATMOSPHERE_PHENOMENA",
  DUST_STORM: "EXTREME_PHENOMENA", FLOOD: "EXTREME_PHENOMENA",
};

const CLIMA_CAT_META = {
  TEMPERATURE:           { label: "Температура",         icon: "🌡️", color: "#ef4444" },
  MOISTURE:              { label: "Вологість",            icon: "💧", color: "#3b82f6" },
  WIND:                  { label: "Вітер",               icon: "💨", color: "#8b5cf6" },
  SNOW:                  { label: "Сніг / Лід",          icon: "❄️", color: "#60a5fa" },
  DISEASES_PESTS:        { label: "Хвороби/шкідники",    icon: "🦠", color: "#f59e0b" },
  SOLAR_RADIATION:       { label: "Сонце",               icon: "☀️", color: "#f97316" },
  ATMOSPHERE_PHENOMENA:  { label: "Атмосфера",           icon: "⛈️", color: "#6366f1" },
  EXTREME_PHENOMENA:     { label: "Екстремальні",        icon: "🌪️", color: "#dc2626" },
};

const EVENT_POINT_META = {
  WEED:    { label: "Бур'ян",  icon: "🌿", color: "#ef4444" },
  PEST:    { label: "Шкідник", icon: "🐛", color: "#f97316" },
  DISEASE: { label: "Хвороба", icon: "🦠", color: "#eab308" },
  OTHER:   { label: "Інше",    icon: "📌", color: "#94a3b8" },
};

function pickFirstNumber(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    const x = Number(v);
    if (Number.isFinite(x)) return x;
  }
  return null;
}

function getListFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.content)) return data.content;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

function getSnapshotFieldId(s) {
  if (!s) return null;
  const v =
    s.fieldId ?? s.fieldID ?? s.field_id ?? s.field?.id ?? s.field?.fieldId ??
    s.field?.field_id ?? s.fieldDto?.id ?? s.fieldDto?.fieldId ??
    s.fieldProjection?.id ?? s.fieldProjection?.fieldId ?? null;
  if (v == null) return null;
  return String(v);
}

function getTaskFieldId(t) {
  if (!t) return null;
  const v =
    t.fieldId ?? t.fieldID ?? t.field_id ?? t.field?.id ?? t.field?.fieldId ??
    t.field?.field_id ?? t.fieldDto?.id ?? t.fieldDto?.fieldId ??
    t.fieldProjection?.id ?? t.fieldProjection?.fieldId ??
    t.fieldSnapshot?.fieldId ?? t.snapshot?.fieldId ?? t.snapshot?.field?.id ?? null;
  return v == null ? null : String(v);
}

function getTaskTitle(t) {
  return t?.title ?? t?.name ?? t?.taskName ?? t?.taskTitle ?? t?.subject ?? "Задача";
}

function getTaskStatus(t) {
  return String(t?.taskStatus || t?.status || "PLANNED").toUpperCase();
}

function getTaskDateValue(t) {
  return (
    t?.plannedEndAt ?? t?.planned_end_at ?? t?.plannedEnd ?? t?.planned_end ??
    t?.endAt ?? t?.end_at ?? t?.deadline ?? t?.deadlineAt ?? t?.dueDate ?? t?.dateTo ?? null
  );
}

function getTaskStartValue(t) {
  return (
    t?.plannedStartAt ?? t?.planned_start_at ?? t?.plannedStart ?? t?.planned_start ??
    t?.startAt ?? t?.start_at ?? t?.dateFrom ?? null
  );
}

function parseDateToMs(v) {
  if (!v) return null;
  const d = new Date(String(v));
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatTaskDate(v) {
  const ms = parseDateToMs(v);
  if (!Number.isFinite(ms)) return "без дати";
  return new Date(ms).toLocaleDateString("uk-UA");
}

function daysBetween(a, b) {
  return Math.round((a - b) / 86400000);
}

function buildTaskAggByFieldId(tasks) {
  const now = Date.now();
  const m = new Map();
  const ensure = (fid) => {
    const key = String(fid);
    if (!m.has(key)) m.set(key, { active: 0, overdue: 0, blocked: 0, total: 0 });
    return m.get(key);
  };
  for (const t of Array.isArray(tasks) ? tasks : []) {
    const fid = getTaskFieldId(t);
    if (!fid) continue;
    const st = getTaskStatus(t);
    const endMs = parseDateToMs(getTaskDateValue(t));
    const hasEnd = Number.isFinite(endMs);
    const agg = ensure(fid);
    agg.total += 1;
    if (st === "BLOCKED") agg.blocked += 1;
    if (st === "PLANNED" || st === "IN_PROGRESS") agg.active += 1;
    if (st !== "DONE" && st !== "CANCELLED" && hasEnd && endMs < now) agg.overdue += 1;
  }
  return m;
}

function riskTone(lvl) {
  const x = String(lvl || "").toUpperCase();
  if (x === "HIGH") return "bad";
  if (x === "MEDIUM") return "warn";
  if (x === "LOW") return "ok";
  return "default";
}

function riskLabel(lvl) {
  const x = String(lvl || "").toUpperCase();
  if (x === "HIGH") return "Високий";
  if (x === "MEDIUM") return "Середній";
  if (x === "LOW") return "Низький";
  return "Не обрано";
}

const YIELD_CATEGORIES = [
  { value: "GRAINS",     label: "Зернові",  icon: "🌾", color: "#f59e0b" },
  { value: "OILSEEDS",   label: "Олійні",   icon: "🌻", color: "#f97316" },
  { value: "LEGUMES",    label: "Бобові",   icon: "🫘", color: "#60a5fa" },
  { value: "VEGETABLES", label: "Овочі",    icon: "🥦", color: "#22c55e" },
  { value: "FRUITS",     label: "Фрукти",   icon: "🍎", color: "#f43f5e" },
  { value: "INDUSTRIAL", label: "Технічні", icon: "🌿", color: "#a3e635" },
  { value: "FORAGE",     label: "Кормові",  icon: "🌱", color: "#34d399" },
  { value: "OTHER",      label: "Інше",     icon: "📦", color: "#94a3b8" },
];

function shortText(v, max = 44) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (s.length <= max) return s;
  return `${s.slice(0, max)}…`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const token = process.env.REACT_APP_MAPBOX_TOKEN;

  const nowYear = new Date().getFullYear();

  const dashboardYears = useMemo(
    () => [nowYear - 2, nowYear - 1, nowYear, nowYear + 1, nowYear + 2],
    [nowYear]
  );

  const cropYears = useMemo(
    () => [nowYear - 3, nowYear - 2, nowYear - 1, nowYear, nowYear + 1],
    [nowYear]
  );

  const [year, setYear] = useState(nowYear);
  const [cropYearFilter, setCropYearFilter] = useState(String(nowYear));
  const [mode, setMode] = useState(MODES.CROP);
  const [taskPeriodDays, setTaskPeriodDays] = useState(30);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [fields, setFields] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [tasks, setTasks] = useState([]);

  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [showAllOnMap, setShowAllOnMap] = useState(false);

  const [manualRiskById, setManualRiskById] = useState({});
  const [savingRiskId, setSavingRiskId] = useState(null);
  const [riskNoteById, setRiskNoteById] = useState({});
  const [riskNoteInput, setRiskNoteInput] = useState("");

  const [allYieldZones, setAllYieldZones] = useState([]);
  const [yieldZonesLoading, setYieldZonesLoading] = useState(false);
  const [selectedYieldYear, setSelectedYieldYear] = useState(null);
  const [allClimateZones, setAllClimateZones] = useState([]);
  const [climateZonesLoading, setClimateZonesLoading] = useState(false);
  const [allFieldEvents, setAllFieldEvents] = useState([]);
  const [fieldEventsLoading, setFieldEventsLoading] = useState(false);

  const listRef = useRef(null);

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const loadAll = useCallback(async () => {
    if (!user) return;
    if (!token) {
      setErr("Немає REACT_APP_MAPBOX_TOKEN у .env (і перезапусти npm start).");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const resMap = await apiFetchJson(user, `/api/dashboard/map?year=${year}&mode=${mode}`, {}, navigate);
      if (!resMap.ok) {
        setFields([]); setSnapshots([]);
        setErr(resMap.error || "Не вдалося завантажити /api/dashboard/map");
        setLoading(false);
        return;
      }
      const data = resMap.data || {};
      const fArr = Array.isArray(data.fields) ? data.fields : [];
      let sArr = Array.isArray(data.snapshots) ? data.snapshots : [];
      fArr.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "uk"));
      setFields(fArr);

      const tRes = await apiFetchJson(user, "/api/tasks", { method: "GET" }, navigate);
      if (tRes.ok) setTasks(getListFromResponse(tRes.data));
      else setTasks([]);

      const looksLikeNoTaskStats =
        sArr.length > 0 &&
        sArr.every((s) => {
          const a = s?.activeTasksCount ?? s?.tasksActive ?? s?.active ?? s?.taskStats?.active ?? s?.taskStats?.activeCount ?? s?.stats?.active ?? null;
          const o = s?.overdueTasksCount ?? s?.tasksOverdue ?? s?.overdue ?? s?.taskStats?.overdue ?? s?.taskStats?.overdueCount ?? s?.stats?.overdue ?? null;
          const b = s?.blockedTasksCount ?? s?.tasksBlocked ?? s?.blocked ?? s?.taskStats?.blocked ?? s?.taskStats?.blockedCount ?? s?.stats?.blocked ?? null;
          return a == null && o == null && b == null;
        });

      if (looksLikeNoTaskStats) {
        const resSnap = await apiFetchJson(user, `/api/dashboard/snapshots?year=${year}`, {}, navigate);
        if (resSnap.ok) {
          const extra = Array.isArray(resSnap.data) ? resSnap.data
            : Array.isArray(resSnap.data?.snapshots) ? resSnap.data.snapshots : [];
          if (extra.length) sArr = extra;
        }
      }
      setSnapshots(sArr);

      const resKpi = await apiFetchJson(user, "/api/dashboard/kpi", {}, navigate);
      if (resKpi.ok) setKpi(resKpi.data || null);

      if (fArr.length) setSelectedFieldId(prev => prev || String(fArr[0].id));
    } catch {
      setFields([]); setSnapshots([]); setTasks([]);
      setErr("Помилка завантаження Dashboard");
    } finally {
      setLoading(false);
    }
  }, [user, year, mode, navigate, token]);

  useEffect(() => { loadAll().catch(() => {}); }, [loadAll]);

  useEffect(() => {
    if (!user || fields.length === 0) return;
    setYieldZonesLoading(true);
    setAllYieldZones([]);
    apiFetchJson(user, `/api/fields/${fields[0].id}/yield-zones`).then(probe => {
      if (!probe.ok && probe.status === 401) return [];
      const rest = fields.slice(1).map(f => apiFetchJson(user, `/api/fields/${f.id}/yield-zones`));
      return Promise.allSettled([Promise.resolve(probe), ...rest.map(p => p.then(v => v))]);
    }).then(results => {
      if (!results) return;
      const zones = [];
      results.forEach((r, i) => {
        const val = r.status === "fulfilled" ? r.value : null;
        if (val?.ok) getListFromResponse(val.data).forEach(z => zones.push({ ...z, __fieldId: String(fields[i].id) }));
      });
      setAllYieldZones(zones);
    }).finally(() => setYieldZonesLoading(false));
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || fields.length === 0) return;
    setClimateZonesLoading(true);
    apiFetchJson(user, `/api/climate-zones/field/${fields[0].id}`).then(probe => {
      if (!probe.ok && probe.status === 401) return [];
      const rest = fields.slice(1).map(f => apiFetchJson(user, `/api/climate-zones/field/${f.id}`));
      return Promise.allSettled([Promise.resolve(probe), ...rest.map(p => p.then(v => v))]);
    }).then(results => {
      if (!results) return;
      const all = [];
      results.forEach((r, i) => {
        const val = r.status === "fulfilled" ? r.value : null;
        if (val?.ok) getListFromResponse(val.data).forEach(z => all.push({ ...z, __fieldId: String(fields[i].id) }));
      });
      setAllClimateZones(all);
    }).finally(() => setClimateZonesLoading(false));
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || fields.length === 0) return;
    setFieldEventsLoading(true);
    apiFetchJson(user, `/api/fields/${fields[0].id}/problem-events`).then(probe => {
      if (!probe.ok && probe.status === 401) return [];
      const rest = fields.slice(1).map(f => apiFetchJson(user, `/api/fields/${f.id}/problem-events`));
      return Promise.allSettled([Promise.resolve(probe), ...rest.map(p => p.then(v => v))]);
    }).then(results => {
      if (!results) return;
      const all = [];
      results.forEach((r, i) => {
        const val = r.status === "fulfilled" ? r.value : null;
        if (val?.ok) getListFromResponse(val.data).forEach(e => all.push({ ...e, __fieldId: String(fields[i].id) }));
      });
      setAllFieldEvents(all);
    }).finally(() => setFieldEventsLoading(false));
  }, [fields]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedFieldId) return;
    const root = listRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-field-id="${selectedFieldId}"]`);
    if (!el) return;
    try { el.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch {}
  }, [selectedFieldId]);

  const snapByFieldId = useMemo(() => {
    const m = new Map();
    for (const s of Array.isArray(snapshots) ? snapshots : []) {
      const fid = getSnapshotFieldId(s);
      if (fid) m.set(fid, s);
    }
    return m;
  }, [snapshots]);

  const taskAggByFieldId = useMemo(() => buildTaskAggByFieldId(tasks), [tasks]);

  // Total zone ha per field for the selected year — used to populate map popup + field list
  const yieldZoneHaByFieldId = useMemo(() => {
    const m = new Map();
    for (const z of allYieldZones) {
      const match = /^(\d{4})\s*\//.exec((z.name ?? "").trim());
      const zYear = match ? Number(match[1]) : null;
      // null-year zones treated as current year (matches YieldPage logic)
      const effectiveYear = zYear ?? nowYear;
      if (effectiveYear !== year) continue;
      const fid = z.__fieldId;
      m.set(fid, (m.get(fid) ?? 0) + Number(z.ha ?? 0));
    }
    return m;
  }, [allYieldZones, year, nowYear]);

  const enrichedFields = useMemo(() => {
    const arr = Array.isArray(fields) ? fields : [];
    return arr.map((f) => {
      const idRaw = f?.id ?? f?.fieldId ?? f?.field_id ?? f?.fieldID;
      const fid = idRaw == null ? null : String(idRaw);
      const s = fid ? snapByFieldId.get(fid) : null;
      const ta = fid ? taskAggByFieldId.get(fid) : null;
      const cropName = s?.cropName ?? s?.crop?.name ?? s?.cropDto?.name ?? s?.crop?.title ?? null;
      const cropId = s?.cropId ?? s?.crop?.id ?? s?.cropDto?.id ?? null;
      const snapActive = pickFirstNumber(s, ["activeTasksCount", "tasksActive", "active", "activeCount"]) ?? pickFirstNumber(s?.taskStats, ["active", "activeCount"]) ?? pickFirstNumber(s?.stats, ["active", "activeCount"]) ?? null;
      const snapOverdue = pickFirstNumber(s, ["overdueTasksCount", "tasksOverdue", "overdue", "overdueCount"]) ?? pickFirstNumber(s?.taskStats, ["overdue", "overdueCount"]) ?? pickFirstNumber(s?.stats, ["overdue", "overdueCount"]) ?? null;
      const snapBlocked = pickFirstNumber(s, ["blockedTasksCount", "tasksBlocked", "blocked", "blockedCount"]) ?? pickFirstNumber(s?.taskStats, ["blocked", "blockedCount"]) ?? pickFirstNumber(s?.stats, ["blocked", "blockedCount"]) ?? null;
      const activeTasksCount = snapActive ?? (ta ? ta.active : null);
      const overdueTasksCount = snapOverdue ?? (ta ? ta.overdue : null);
      const blockedTasksCount = snapBlocked ?? (ta ? ta.blocked : null);
      const yieldZoneHa = fid ? (yieldZoneHaByFieldId.get(fid) ?? null) : null;
      const snapRiskLevel = s?.riskLevel ?? s?.risk ?? s?.risk?.level ?? s?.stats?.riskLevel ?? null;
      const riskScore = pickFirstNumber(s, ["riskScore"]) ?? pickFirstNumber(s?.risk, ["score"]) ?? null;
      const manual = fid ? manualRiskById[fid] : undefined;
      const finalRisk = (manual !== undefined ? manual : snapRiskLevel) ?? "";
      return {
        ...f,
        id: fid ?? idRaw,
        fieldId: fid ?? idRaw,
        fieldName: f?.name ?? f?.fieldName ?? (fid ? `Поле #${fid}` : "Поле"),
        geometryJson: f?.geometryJson ?? f?.geometry_json ?? f?.geometry ?? null,
        cropName, cropId,
        cropCategory: String(s?.crop?.category ?? s?.cropDto?.category ?? s?.cropCategory ?? "").toUpperCase() || null,
        yieldValue: yieldZoneHa,
        yieldZoneHa,
        riskLevel: String(finalRisk || "").toUpperCase(),
        riskScore,
        activeTasksCount: Number.isFinite(Number(activeTasksCount)) ? Number(activeTasksCount) : 0,
        overdueTasksCount: Number.isFinite(Number(overdueTasksCount)) ? Number(overdueTasksCount) : 0,
        blockedTasksCount: Number.isFinite(Number(blockedTasksCount)) ? Number(blockedTasksCount) : 0,
        areaHa: pickFirstNumber(f, ["areaHa", "area", "area_ha", "square", "areaSquare"]) ?? pickFirstNumber(s, ["areaHa", "area", "area_ha"]) ?? null,
      };
    });
  }, [fields, snapByFieldId, taskAggByFieldId, manualRiskById, yieldZoneHaByFieldId]);

  const fieldNameById = useMemo(() => {
    const m = new Map();
    for (const f of enrichedFields) m.set(String(f.id), f.fieldName);
    return m;
  }, [enrichedFields]);

  const listFields = useMemo(() => {
    const arr = [...enrichedFields];
    if (mode !== MODES.RISK) return arr;
    const rank = (lvl) => {
      const x = String(lvl || "").toUpperCase();
      if (x === "HIGH") return 0; if (x === "MEDIUM") return 1; if (x === "LOW") return 2; return 3;
    };
    arr.sort((a, b) => {
      const ra = rank(a?.riskLevel), rb = rank(b?.riskLevel);
      if (ra !== rb) return ra - rb;
      return String(a?.fieldName || "").localeCompare(String(b?.fieldName || ""), "uk");
    });
    return arr;
  }, [enrichedFields, mode]);

  const summary = useMemo(() => {
    const totalFields = enrichedFields.length;
    const totalArea = enrichedFields.reduce((acc, f) => acc + safeNum(f?.areaHa, 0), 0);
    const active = enrichedFields.reduce((acc, f) => acc + safeNum(f?.activeTasksCount, 0), 0);
    const overdue = enrichedFields.reduce((acc, f) => acc + safeNum(f?.overdueTasksCount, 0), 0);
    const blocked = enrichedFields.reduce((acc, f) => acc + safeNum(f?.blockedTasksCount, 0), 0);
    const yields = enrichedFields.map((f) => Number(f?.yieldValue)).filter((x) => Number.isFinite(x));
    const avgYield = yields.length ? yields.reduce((a, b) => a + b, 0) / yields.length : null;
    return { totalFields, totalArea, active, overdue, blocked, avgYield };
  }, [enrichedFields]);

  const selectedField = useMemo(() => {
    if (!selectedFieldId) return null;
    return enrichedFields.find((f) => String(f?.id) === String(selectedFieldId)) || null;
  }, [enrichedFields, selectedFieldId]);

  const taskPanels = useMemo(() => {
    const now = Date.now();
    const periodMs = taskPeriodDays * 86400000;
    const overdueRecent = [], activeUpcoming = [];
    for (const t of Array.isArray(tasks) ? tasks : []) {
      const st = getTaskStatus(t);
      const isDone = st === "DONE", isCancelled = st === "CANCELLED";
      const isBlocked = st === "BLOCKED", isInProgress = st === "IN_PROGRESS", isPlanned = st === "PLANNED";
      const endValue = getTaskDateValue(t);
      const endMs = parseDateToMs(endValue);
      const fid = getTaskFieldId(t);
      const row = {
        raw: t,
        id: String(t?.id ?? t?.taskId ?? t?.uuid ?? `${getTaskTitle(t)}-${getTaskDateValue(t) || "nodate"}`),
        title: getTaskTitle(t),
        fieldId: fid,
        fieldName: fid ? fieldNameById.get(String(fid)) || `Поле #${fid}` : "Без поля",
        status: st, deadline: endValue, deadlineMs: endMs,
        startValue: getTaskStartValue(t),
        daysDiff: Number.isFinite(endMs) ? daysBetween(endMs, now) : null,
      };
      if (!isDone && !isCancelled && Number.isFinite(endMs) && endMs < now) {
        if (now - endMs <= periodMs) overdueRecent.push(row);
      }
      if (!isDone && !isCancelled && !isBlocked && (isPlanned || isInProgress)) {
        if (!Number.isFinite(endMs)) activeUpcoming.push(row);
        else if (endMs >= now && endMs <= now + periodMs) activeUpcoming.push(row);
      }
    }
    const byMs = (a, b) => {
      if (a.deadlineMs == null && b.deadlineMs == null) return 0;
      if (a.deadlineMs == null) return 1; if (b.deadlineMs == null) return -1;
      return a.deadlineMs - b.deadlineMs;
    };
    overdueRecent.sort(byMs); activeUpcoming.sort(byMs);
    return { overdueRecent, activeUpcoming };
  }, [tasks, taskPeriodDays, fieldNameById]);

  const yieldZoneStats = useMemo(() => {
    const forYear = allYieldZones.filter(z => {
      const m = /^(\d{4})\s*\//.exec((z.name ?? "").trim());
      const zYear = m ? Number(m[1]) : null;
      const effectiveYear = zYear ?? nowYear;
      return effectiveYear === year;
    });
    const totalZones = forYear.length;
    const totalHa = forYear.reduce((s, z) => s + Number(z.ha ?? 0), 0);
    const byCat = new Map();
    for (const z of forYear) {
      const k = String(z.cropCategory ?? "OTHER");
      const p = byCat.get(k) || { zones: 0, ha: 0 };
      p.zones += 1; p.ha += Number(z.ha ?? 0);
      byCat.set(k, p);
    }
    const categories = YIELD_CATEGORIES
      .map(c => ({ ...c, zones: byCat.get(c.value)?.zones ?? 0, ha: byCat.get(c.value)?.ha ?? 0, percent: totalHa > 0 ? (byCat.get(c.value)?.ha ?? 0) / totalHa * 100 : 0 }))
      .filter(c => c.zones > 0).sort((a, b) => b.ha - a.ha);
    return { totalZones, totalHa, categories };
  }, [allYieldZones, year, nowYear]);

  // Crop/zone breakdown from yield zones, filtered by year — replaces snapshot-based crop analytics
  const yieldZoneCropRows = useMemo(() => {
    const targetYear = cropYearFilter === "ALL" ? null : Number(cropYearFilter);
    const filtered = allYieldZones.filter(z => {
      if (!targetYear) return true;
      const m = /^(\d{4})\s*\//.exec((z.name ?? "").trim());
      const zYear = m ? Number(m[1]) : null;
      const effectiveYear = zYear ?? nowYear;
      return effectiveYear === targetYear;
    });
    const byCrop = new Map();
    for (const z of filtered) {
      const m = /^(\d{4})\s*\/\s*(.+)$/.exec((z.name ?? "").trim());
      const cropName = m ? m[2].trim() : (z.name ?? "—").trim() || "—";
      const catKey = String(z.cropCategory ?? "OTHER");
      const ha = Number(z.ha ?? 0);
      const key = `${catKey}::${cropName}`;
      if (!byCrop.has(key)) byCrop.set(key, { cropName, catKey, totalHa: 0, zones: 0, fields: new Set() });
      const r = byCrop.get(key);
      r.totalHa += ha;
      r.zones++;
      r.fields.add(z.__fieldId);
    }
    const maxHa = Math.max(0, ...[...byCrop.values()].map(r => r.totalHa));
    const totalHa = [...byCrop.values()].reduce((s, r) => s + r.totalHa, 0);
    return [...byCrop.values()]
      .map(r => {
        const cat = YIELD_CATEGORIES.find(c => c.value === r.catKey) ?? { value: r.catKey, label: r.catKey || "Інше", icon: "🌱", color: "#94a3b8" };
        return {
          cropName: r.cropName, catKey: r.catKey, cat,
          totalHa: r.totalHa, zones: r.zones, fieldsCount: r.fields.size,
          percent: maxHa > 0 ? (r.totalHa / maxHa) * 100 : 0,
          share: totalHa > 0 ? (r.totalHa / totalHa) * 100 : 0,
        };
      })
      .sort((a, b) => b.totalHa - a.totalHa);
  }, [allYieldZones, cropYearFilter, nowYear]);

  const riskSummary = useMemo(() => ({
    high:   enrichedFields.filter(f => f.riskLevel === "HIGH").length,
    medium: enrichedFields.filter(f => f.riskLevel === "MEDIUM").length,
    low:    enrichedFields.filter(f => f.riskLevel === "LOW").length,
    noData: enrichedFields.filter(f => !f.riskLevel || f.riskLevel === "NO_DATA" || f.riskLevel === "").length,
  }), [enrichedFields]);

  useEffect(() => {
    if (selectedFieldId) setRiskNoteInput(riskNoteById[selectedFieldId] ?? "");
  }, [selectedFieldId, riskNoteById]);

  const persistRisk = useCallback(async (fieldId, riskLevel, note = "") => {
    if (!user || !fieldId) return;
    const fid = String(fieldId);
    const lvl = String(riskLevel || "").trim().toUpperCase();
    const payload = { fieldId: Number(fid), year: Number(year), riskLevel: lvl ? lvl : "NO_DATA", note: note.trim() || null };
    setSavingRiskId(fid); setErr("");
    try {
      const res = await apiFetchJson(user, "/api/dashboard/risk", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }, navigate);
      if (!res?.ok) { setErr(res?.error || "Не вдалося зберегти ризик."); return; }
      setManualRiskById((prev) => { const next = { ...prev }; delete next[fid]; return next; });
      if (note.trim()) setRiskNoteById((prev) => ({ ...prev, [fid]: note.trim() }));
      await loadAll();
    } catch {
      setErr("Помилка при збереженні ризику.");
    } finally {
      setSavingRiskId(null);
    }
  }, [user, year, navigate, loadAll]);

  const setManualRisk = useCallback(async (fieldId, nextLevel) => {
    const fid = String(fieldId);
    const lvl = String(nextLevel || "").toUpperCase();
    setManualRiskById((prev) => ({ ...prev, [fid]: lvl }));
    await persistRisk(fid, lvl, riskNoteInput);
  }, [persistRisk, riskNoteInput]);

  // ── Врожайність по роках (з зон врожайності) ──────────────────────────────
  const yieldByYear = useMemo(() => {
    const byYear = new Map();
    for (const z of allYieldZones) {
      const m = /^(\d{4})\s*\//.exec((z.name ?? "").trim());
      const zYear = m ? Number(m[1]) : null;
      const y = zYear ?? nowYear; // null-year zones treated as current year
      if (y < 2010 || y > 2100) continue;
      if (!byYear.has(y)) byYear.set(y, { year: y, totalHa: 0, zones: 0, fields: new Set(), cats: new Map() });
      const r = byYear.get(y);
      r.totalHa += safeNum(z.ha ?? 0, 0);
      r.zones++;
      r.fields.add(z.__fieldId);
      const cat = String(z.cropCategory ?? "").trim();
      if (cat) r.cats.set(cat, (r.cats.get(cat) ?? 0) + safeNum(z.ha ?? 0, 0));
    }
    const maxHa = Math.max(0, ...Array.from(byYear.values()).map(r => r.totalHa));
    return Array.from(byYear.values())
      .map(r => {
        const topCatKey = [...r.cats.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
        const topCat = YIELD_CATEGORIES.find(c => c.value === topCatKey) ?? null;
        return {
          year: r.year,
          totalHa: r.totalHa,
          zonesCount: r.zones,
          fieldsCount: r.fields.size,
          topCat,
          pct: maxHa > 0 ? (r.totalHa / maxHa) * 100 : 0,
        };
      })
      .sort((a, b) => b.year - a.year)
      .slice(0, 5);
  }, [allYieldZones, nowYear]);

  const yieldActiveYear = useMemo(() => {
    if (!yieldByYear.length) return null;
    return yieldByYear.find(r => r.year === selectedYieldYear) ?? yieldByYear[0];
  }, [yieldByYear, selectedYieldYear]);

  const yieldByField = useMemo(() => {
    if (!yieldActiveYear) return [];
    const ay = yieldActiveYear.year;
    const byField = new Map();
    for (const z of allYieldZones) {
      const m = /^(\d{4})\s*\//.exec((z.name ?? "").trim());
      const zYear = m ? Number(m[1]) : null;
      const y = zYear ?? nowYear;
      if (y !== ay) continue;
      const fid = z.__fieldId;
      if (!byField.has(fid)) byField.set(fid, { fieldId: fid, zones: 0, totalHa: 0, cats: new Map() });
      const r = byField.get(fid);
      r.zones++;
      r.totalHa += safeNum(z.ha ?? 0, 0);
      const cat = String(z.cropCategory ?? "").trim();
      if (cat) r.cats.set(cat, (r.cats.get(cat) ?? 0) + safeNum(z.ha ?? 0, 0));
    }
    return Array.from(byField.values())
      .map(r => {
        const topCatKey = [...r.cats.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
        const topCat = YIELD_CATEGORIES.find(c => c.value === topCatKey) ?? null;
        return { ...r, fieldName: fieldNameById.get(r.fieldId) ?? `Поле #${r.fieldId}`, topCat };
      })
      .sort((a, b) => b.totalHa - a.totalHa);
  }, [allYieldZones, yieldActiveYear, fieldNameById, nowYear]);

  // ── Кліматичні зони: зведення ───────────────────────────────────────────────
  const climateZoneSummary = useMemo(() => {
    const byCat = new Map();
    const risk = { HIGH: 0, MEDIUM: 0, LOW: 0 };
    const fieldSet = new Set();
    for (const z of allClimateZones) {
      const catKey = CLIMA_SUBTYPE_TO_CAT[z.climatSubType] ?? "TEMPERATURE";
      const meta   = CLIMA_CAT_META[catKey] ?? CLIMA_CAT_META.TEMPERATURE;
      if (!byCat.has(catKey)) byCat.set(catKey, { ...meta, catKey, zones: 0, fields: new Set(), high: 0, medium: 0, low: 0 });
      const r = byCat.get(catKey);
      r.zones++;
      r.fields.add(z.__fieldId);
      fieldSet.add(z.__fieldId);
      if (z.riskLevel === "HIGH")        { r.high++;   risk.HIGH++;   }
      else if (z.riskLevel === "MEDIUM") { r.medium++; risk.MEDIUM++; }
      else                               { r.low++;    risk.LOW++;    }
    }
    const categories = Array.from(byCat.values())
      .map(r => ({ ...r, fieldsCount: r.fields.size }))
      .sort((a, b) => (b.high - a.high) || (b.zones - a.zones));
    return { total: allClimateZones.length, fieldsAffected: fieldSet.size, categories, risk };
  }, [allClimateZones]);

  // ── Проблемні події: зведення ───────────────────────────────────────────────
  const eventSummary = useMemo(() => {
    const counts = { WEED: 0, PEST: 0, DISEASE: 0, OTHER: 0 };
    const byField = new Map();
    for (const ev of allFieldEvents) {
      const pts = Array.isArray(ev.points) ? ev.points : [];
      const fid = ev.__fieldId;
      if (fid && !byField.has(fid)) byField.set(fid, { fieldId: fid, events: 0, points: 0 });
      if (fid) { byField.get(fid).events++; byField.get(fid).points += pts.length; }
      for (const pt of pts) {
        const t = String(pt.pointType ?? pt.type ?? "OTHER").toUpperCase();
        if (counts[t] !== undefined) counts[t]++; else counts.OTHER++;
      }
    }
    const byFieldList = Array.from(byField.values())
      .map(r => ({ ...r, fieldName: fieldNameById.get(r.fieldId) ?? `Поле #${r.fieldId}` }))
      .sort((a, b) => b.events - a.events)
      .slice(0, 8);
    const totalPoints = Object.values(counts).reduce((s, v) => s + v, 0);
    const byType = Object.entries(EVENT_POINT_META).map(([key, meta]) => ({
      ...meta, key, count: counts[key],
      pct: totalPoints > 0 ? (counts[key] / totalPoints) * 100 : 0,
    }));
    return { total: allFieldEvents.length, byType, byFieldList };
  }, [allFieldEvents, fieldNameById]);

  const openTasksWithPreset = useCallback((kind) => {
    const params = new URLSearchParams();
    params.set("dashboard", "1");
    params.set("periodDays", String(taskPeriodDays));
    if (kind === "overdue") { params.set("preset", "OVERDUE"); params.set("status", "OPEN"); }
    else { params.set("preset", "ACTIVE"); params.set("status", "ACTIVE"); }
    navigate(`/tasks?${params.toString()}`, { state: { dashboardPreset: kind, periodDays: taskPeriodDays, source: "dashboard" } });
  }, [navigate, taskPeriodDays]);

  const S = {
    page: { padding: "16px 20px", color: "var(--text)", width: "100%", maxWidth: "100%", boxSizing: "border-box" },

    hRow: { display: "flex", alignItems: "center", gap: 10, paddingBottom: 16, flexWrap: "wrap" },

    hTitle: { margin: 0, fontWeight: 950, fontSize: "clamp(20px, 2.2vw, 30px)", color: "var(--text)", lineHeight: 1.1, flexShrink: 0 },

    datePill: {
      padding: "7px 12px", borderRadius: 999, border: "1px solid var(--border)",
      background: "var(--panel2)", fontSize: 12, fontWeight: 700, color: "var(--muted)", flexShrink: 0, whiteSpace: "nowrap",
    },

    select: {
      borderRadius: 10, padding: "8px 10px", fontWeight: 800,
      border: "1px solid var(--border)", background: "var(--panel2)", color: "var(--text)",
      outline: "none", boxShadow: "var(--shadow-soft)", minHeight: 38,
    },

    tabs: {
      display: "flex", borderRadius: 10, border: "1px solid var(--border)",
      background: "var(--panel2)", overflow: "hidden", flexShrink: 0, boxShadow: "var(--shadow-soft)",
    },

    tab: (active) => ({
      padding: "8px 14px", fontWeight: 900, fontSize: 13, cursor: "pointer", border: "none",
      background: active ? "rgba(31,143,80,.18)" : "transparent",
      color: "var(--text)", transition: ".15s ease", whiteSpace: "nowrap",
    }),

    refreshBtn: {
      padding: "8px 14px", borderRadius: 10, border: "1px solid var(--border)",
      background: "var(--panel2)", color: "var(--text)", fontWeight: 900,
      cursor: "pointer", boxShadow: "var(--shadow-soft)", minHeight: 38, whiteSpace: "nowrap",
    },

    meta: { marginLeft: "auto", fontSize: 12, fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" },

    errorBox: {
      marginBottom: 12, padding: "10px 14px", borderRadius: 12,
      background: "rgba(239,68,68,.10)", border: "1px solid rgba(239,68,68,.30)",
      color: "var(--text)", fontWeight: 900,
    },

    kpiGrid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 14 },

    kpiCard: (tone = "default") => ({
      borderRadius: 16, padding: "14px 16px",
      border:
        tone === "bad"  ? "1px solid rgba(239,68,68,.22)"
        : tone === "warn" ? "1px solid rgba(245,158,11,.22)"
        : tone === "ok"   ? "1px solid rgba(34,197,94,.22)"
        : "1px solid var(--border)",
      background:
        tone === "bad"  ? "linear-gradient(135deg, rgba(239,68,68,.08), rgba(239,68,68,.02))"
        : tone === "warn" ? "linear-gradient(135deg, rgba(245,158,11,.08), rgba(245,158,11,.02))"
        : tone === "ok"   ? "linear-gradient(135deg, rgba(34,197,94,.08), rgba(34,197,94,.02))"
        : "linear-gradient(135deg, var(--panel2), var(--panel))",
      boxShadow: "var(--shadow-soft)", display: "flex", alignItems: "center", gap: 14, minWidth: 0, transition: ".18s ease",
    }),

    kpiIconCircle: (tone = "default") => ({
      width: 46, height: 46, borderRadius: 14, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
      background:
        tone === "bad"  ? "rgba(239,68,68,.16)"
        : tone === "warn" ? "rgba(245,158,11,.16)"
        : tone === "ok"   ? "rgba(34,197,94,.16)"
        : "rgba(31,143,80,.10)",
    }),

    kpiBody: { minWidth: 0, flex: "1 1 auto" },
    kpiValue: { fontSize: "clamp(18px, 1.6vw, 24px)", fontWeight: 1000, color: "var(--text)", lineHeight: 1.1, overflowWrap: "anywhere" },
    kpiLabel: { fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 3 },

    card: { borderRadius: 20, border: "1px solid var(--border)", background: "var(--panel2)", boxShadow: "var(--shadow)", overflow: "hidden", marginBottom: 14 },

    cardHead: {
      padding: "12px 16px", borderBottom: "1px solid var(--border)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 10, flexWrap: "wrap", background: "rgba(255,255,255,.015)",
    },

    cardTitle: { fontWeight: 950, fontSize: 15, color: "var(--text)" },

    mapGrid: { display: "grid", gridTemplateColumns: "1fr 380px", height: 600 },

    mapWrap: { position: "relative", borderRight: "1px solid var(--border)", overflow: "hidden" },

    listBox: { display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 },

    riskSummaryBar: {
      display: "flex", gap: 6, flexWrap: "wrap", padding: "8px 12px",
      borderBottom: "1px solid var(--border)", background: "rgba(255,255,255,.015)",
      alignItems: "center", flexShrink: 0,
    },

    riskEditor: { padding: "10px 12px", borderBottom: "1px solid var(--border)", display: "grid", gap: 9, flexShrink: 0 },

    riskTitle: { fontWeight: 950, color: "var(--text)", fontSize: 13 },
    riskBtnRow: { display: "flex", gap: 6, flexWrap: "wrap" },

    riskBtn: (tone, active) => ({
      borderRadius: 999, padding: "7px 11px", fontWeight: 900, fontSize: 12, cursor: "pointer",
      border:
        tone === "ok"   ? "1px solid rgba(34,197,94,.35)"
        : tone === "warn" ? "1px solid rgba(245,158,11,.35)"
        : tone === "bad"  ? "1px solid rgba(239,68,68,.35)"
        : "1px solid var(--border)",
      background: active
        ? tone === "ok"   ? "rgba(34,197,94,.18)"
          : tone === "warn" ? "rgba(245,158,11,.18)"
          : tone === "bad"  ? "rgba(239,68,68,.18)"
          : "rgba(120,120,120,.16)"
        : "var(--panel)",
      color: "var(--text)", transition: ".15s ease", whiteSpace: "nowrap",
    }),

    riskSub: { fontSize: 11, fontWeight: 700, color: "var(--muted)", overflowWrap: "anywhere" },

    riskNoteField: {
      width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: 8,
      border: "1px solid var(--border)", background: "var(--panel)", color: "var(--text)",
      fontSize: 12, fontWeight: 600, resize: "vertical", outline: "none", fontFamily: "inherit",
    },

    fieldList: {
      flex: "1 1 auto", minHeight: 0, overflowY: "auto", padding: 8,
      display: "grid", gap: 7, alignContent: "start", overscrollBehavior: "contain",
    },

    fieldItem: (active, leftColor) => ({
      display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 14,
      border: leftColor ? `1px solid ${leftColor}33` : active ? "1px solid rgba(31,143,80,.35)" : "1px solid var(--border)",
      borderLeft: leftColor ? `3px solid ${leftColor}` : undefined,
      background: active ? "linear-gradient(135deg, rgba(31,143,80,.12), rgba(31,143,80,.04))" : "var(--panel)",
      cursor: "pointer", boxShadow: active ? "0 6px 18px rgba(31,143,80,.10)" : "var(--shadow-soft)",
      transition: ".15s ease", minWidth: 0,
    }),

    fieldName: { fontWeight: 900, fontSize: 13, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },

    fieldArea: {
      marginLeft: "auto", padding: "5px 9px", borderRadius: 999,
      border: "1px solid var(--border)", background: "var(--panel2)",
      fontWeight: 900, fontSize: 12, whiteSpace: "nowrap", color: "var(--text)", flexShrink: 0,
    },

    chips: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 },

    chip: (tone = "default") => {
      const base = { padding: "4px 8px", borderRadius: 999, fontWeight: 700, fontSize: 11, border: "1px solid var(--border)", background: "var(--panel2)", color: "var(--text)", whiteSpace: "nowrap" };
      if (tone === "ok")   return { ...base, border: "1px solid rgba(34,197,94,.28)", background: "rgba(34,197,94,.10)" };
      if (tone === "warn") return { ...base, border: "1px solid rgba(245,158,11,.28)", background: "rgba(245,158,11,.10)" };
      if (tone === "bad")  return { ...base, border: "1px solid rgba(239,68,68,.28)",  background: "rgba(239,68,68,.10)"  };
      return base;
    },

    empty: { opacity: .75, fontWeight: 700, color: "var(--muted)", padding: 6, fontSize: 13 },

    sectionGrid: { display: "grid", gridTemplateColumns: "1.05fr .95fr", gap: 12, marginBottom: 14 },

    sectionCard: { borderRadius: 20, border: "1px solid var(--border)", background: "var(--panel2)", boxShadow: "var(--shadow-soft)", overflow: "hidden", minWidth: 0 },

    taskPanels: { padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },

    taskPanel: { borderRadius: 16, border: "1px solid var(--border)", background: "var(--panel)", padding: 12, display: "flex", flexDirection: "column", gap: 10, minWidth: 0 },

    taskPanelHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },

    taskPanelTitle: { fontWeight: 950, fontSize: 14, color: "var(--text)" },

    taskCount: (tone = "default") => ({
      padding: "5px 10px", borderRadius: 999, fontWeight: 1000, fontSize: 13,
      border: tone === "warn" ? "1px solid rgba(245,158,11,.28)" : tone === "ok" ? "1px solid rgba(34,197,94,.28)" : "1px solid var(--border)",
      background: tone === "warn" ? "rgba(245,158,11,.10)" : tone === "ok" ? "rgba(34,197,94,.10)" : "var(--panel2)",
      color: "var(--text)", whiteSpace: "nowrap",
    }),

    taskList: { display: "grid", gap: 7, flex: "1 1 auto", minHeight: 0 },

    taskRow: (tone = "default") => ({
      borderRadius: 12,
      border: `1px solid ${tone === "bad" ? "rgba(239,68,68,.20)" : tone === "warn" ? "rgba(245,158,11,.20)" : "rgba(34,197,94,.20)"}`,
      borderLeft: `3px solid ${tone === "bad" ? "#ef4444" : tone === "warn" ? "#f59e0b" : "#22c55e"}`,
      background:
        tone === "bad"  ? "linear-gradient(135deg, rgba(239,68,68,.06), rgba(239,68,68,.02))"
        : tone === "warn" ? "linear-gradient(135deg, rgba(245,158,11,.06), rgba(245,158,11,.02))"
        : "linear-gradient(135deg, rgba(34,197,94,.05), rgba(34,197,94,.01))",
      padding: "8px 10px", display: "grid", gap: 5, minWidth: 0,
    }),

    taskTitle: { fontWeight: 900, fontSize: 13, color: "var(--text)", lineHeight: 1.25, overflowWrap: "anywhere" },
    taskMetaRow: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },

    pill: (tone = "default") => ({
      padding: "4px 8px", borderRadius: 999,
      border: tone === "bad" ? "1px solid rgba(239,68,68,.28)" : tone === "ok" ? "1px solid rgba(34,197,94,.28)" : tone === "warn" ? "1px solid rgba(245,158,11,.28)" : "1px solid var(--border)",
      background: tone === "bad" ? "rgba(239,68,68,.08)" : tone === "ok" ? "rgba(34,197,94,.08)" : tone === "warn" ? "rgba(245,158,11,.08)" : "var(--panel2)",
      fontSize: 11, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap", maxWidth: "100%", overflowWrap: "anywhere",
    }),

    taskFooter: { marginTop: "auto", display: "flex", justifyContent: "flex-end" },

    linkBtn: {
      padding: "7px 12px", borderRadius: 10, border: "1px solid var(--border)",
      background: "var(--panel2)", color: "var(--text)", fontWeight: 900, fontSize: 12,
      cursor: "pointer", boxShadow: "var(--shadow-soft)", whiteSpace: "nowrap",
    },

    cropWrap: { padding: 12, display: "grid", gap: 10 },
    cropStats: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },

    miniStat: { borderRadius: 14, border: "1px solid var(--border)", background: "var(--panel)", padding: "10px 12px", boxShadow: "var(--shadow-soft)", display: "grid", gap: 3, minWidth: 0 },
    miniStatLabel: { fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em" },
    miniStatValue: { fontSize: 17, fontWeight: 1000, color: "var(--text)", overflowWrap: "anywhere" },

    cropList: { display: "grid", gap: 8 },

    cropRow: {
      display: "grid", gridTemplateColumns: "48px minmax(0, 1fr) auto", gap: 10, alignItems: "center",
      borderRadius: 16, border: "1px solid var(--border)", background: "linear-gradient(135deg, var(--panel2), var(--panel))",
      padding: "10px 12px", boxShadow: "var(--shadow-soft)", minWidth: 0,
    },

    cropThumb: { width: 48, height: 48, borderRadius: 14, border: "1px solid var(--border)", overflow: "hidden", background: "rgba(255,255,255,.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 },
    cropThumbImg: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
    cropMain: { minWidth: 0, display: "grid", gap: 6 },
    cropHeadRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" },
    cropName: { fontWeight: 950, fontSize: 13, color: "var(--text)", minWidth: 0, overflowWrap: "anywhere" },
    cropSub: { fontWeight: 700, color: "var(--muted)", fontSize: 11, overflowWrap: "anywhere" },
    progressTrack: { width: "100%", height: 8, borderRadius: 999, background: "rgba(120,120,120,.12)", overflow: "hidden" },
    progressFill: (pct) => ({ width: `${Math.max(4, Math.min(100, pct))}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg, rgba(31,143,80,.7), rgba(44,166,94,.95))", boxShadow: "0 4px 10px rgba(31,143,80,.2)" }),
    cropYield: { minWidth: 100, textAlign: "right", display: "grid", gap: 3, flexShrink: 0 },
    cropYieldValue: { fontWeight: 1000, color: "var(--text)", fontSize: 16, whiteSpace: "nowrap" },
    cropYieldLabel: { color: "var(--muted)", fontWeight: 700, fontSize: 10, whiteSpace: "nowrap" },

    yieldCat: (color) => ({
      borderRadius: 14, border: `1px solid ${color}30`, borderLeft: `3px solid ${color}`,
      background: `${color}06`, padding: "12px 14px", boxShadow: "var(--shadow-soft)", minWidth: 0,
    }),

    footer: { display: "flex", gap: 8, flexWrap: "wrap", padding: "10px 14px", borderRadius: 16, border: "1px solid var(--border)", background: "var(--panel2)", alignItems: "center", boxShadow: "var(--shadow-soft)" },
    footerLabel: { fontWeight: 900, fontSize: 13, color: "var(--text)", marginRight: 4, whiteSpace: "nowrap" },

    // ── Year tabs ─────────────────────────────────────────────────────────────
    yearTabs: { display: "flex", gap: 0, borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden", flexShrink: 0, boxShadow: "var(--shadow-soft)" },
    yearTab: (active) => ({
      padding: "8px 18px", fontWeight: 900, fontSize: 13, cursor: "pointer", border: "none",
      borderRight: "1px solid var(--border)",
      background: active ? "rgba(31,143,80,.18)" : "var(--panel2)",
      color: active ? "#16a34a" : "var(--text)", transition: ".15s ease", whiteSpace: "nowrap",
    }),

    // ── Yield by year ─────────────────────────────────────────────────────────
    yieldYearGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 },
    yieldYearCard: (isCurrent) => ({
      borderRadius: 16, padding: "14px 16px",
      border: isCurrent ? "1px solid rgba(31,143,80,.35)" : "1px solid var(--border)",
      background: isCurrent ? "linear-gradient(135deg, rgba(31,143,80,.10), rgba(31,143,80,.03))" : "var(--panel)",
      boxShadow: "var(--shadow-soft)", minWidth: 0,
    }),
    yieldYearNum: { fontWeight: 1000, fontSize: 22, color: "var(--text)", lineHeight: 1 },
    yieldYearBar: { height: 5, borderRadius: 999, background: "rgba(120,120,120,.12)", overflow: "hidden", margin: "10px 0 8px" },
    yieldYearFill: (pct, isCurrent) => ({ width: `${Math.max(4, pct)}%`, height: "100%", borderRadius: 999, background: isCurrent ? "#16a34a" : "#94a3b8", transition: "width .4s" }),

    // ── Climate zones ─────────────────────────────────────────────────────────
    climaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 },
    climaCard: (color) => ({
      borderRadius: 14, padding: "12px 14px",
      border: `1px solid ${color}28`, borderLeft: `3px solid ${color}`,
      background: `${color}06`, boxShadow: "var(--shadow-soft)", minWidth: 0,
    }),
    riskBar: { display: "flex", height: 6, borderRadius: 999, overflow: "hidden", gap: 2, marginTop: 8 },
    riskSegment: (color, pct) => ({ flex: `0 0 ${Math.max(4, pct)}%`, background: color, borderRadius: 999 }),

    // ── Field events ──────────────────────────────────────────────────────────
    evTypeRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 },
    evTypeCard: (color) => ({
      borderRadius: 14, padding: "12px 14px",
      border: `1px solid ${color}28`, borderTop: `3px solid ${color}`,
      background: "var(--panel)", boxShadow: "var(--shadow-soft)", textAlign: "center",
    }),
    evFieldRow: (i) => ({
      display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
      borderRadius: 10,
      background: i % 2 === 0 ? "var(--panel)" : "transparent",
      border: "1px solid transparent",
    }),
  };

  return (
    <AppShell>
      <div style={S.page}>

        {/* ── Header ───────────────────────────────── */}
        <div style={S.hRow} className="dhHRow">
          <h2 style={S.hTitle} className="dhTitle">Панель керування</h2>

          <span style={S.datePill}>
            {new Date().toLocaleDateString("uk-UA", { day: "2-digit", month: "long", year: "numeric" })}
          </span>

          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={S.select} className="dhControl">
            {dashboardYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>

          <div style={S.tabs} className="dhControl">
            <button style={S.tab(mode === MODES.CROP)} onClick={() => setMode(MODES.CROP)} type="button">🌾 Культури</button>
            <button style={S.tab(mode === MODES.RISK)} onClick={() => setMode(MODES.RISK)} type="button">⚠ Ризики</button>
          </div>

          <button onClick={loadAll} style={S.refreshBtn} disabled={loading} type="button" className="dhControl">
            {loading ? "…" : "↻ Оновити"}
          </button>

          <div style={S.meta} className="dhMeta">Полів: {fmt(enrichedFields.length)} · Задач: {fmt(tasks.length)}</div>
        </div>

        {err ? <div style={S.errorBox}>{String(err)}</div> : null}

        {/* ── KPI Cards ────────────────────────────── */}
        <div style={S.kpiGrid} className="dhKpiGrid">
          {[
            { icon: "🌿", label: "Всього полів",       value: fmt(summary.totalFields),         tone: "default" },
            { icon: "📏", label: "Загальна площа",     value: `${fmt1(kpi?.fieldKpiDto?.sumArea ?? summary.totalArea)} га`, tone: "default" },
            { icon: "✅", label: "Активні задачі",     value: fmt(summary.active),              tone: "ok" },
            { icon: "⏰", label: "Прострочені задачі", value: fmt(summary.overdue),             tone: summary.overdue > 0 ? "warn" : "ok" },
            { icon: "🔴", label: "Поля HIGH ризику",   value: fmt(riskSummary.high),            tone: riskSummary.high > 0 ? "bad" : "ok" },
          ].map(({ icon, label, value, tone }) => (
            <div key={label} style={S.kpiCard(tone)} className="dhKpiCard">
              <div style={S.kpiIconCircle(tone)}>{icon}</div>
              <div style={S.kpiBody}>
                <div style={S.kpiValue}>{loading ? "…" : value}</div>
                <div style={S.kpiLabel}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Map + Fields List ────────────────────── */}
        <div style={S.card}>
          <div style={S.cardHead}>
            <div style={S.cardTitle}>🗺 Поля</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {!showAllOnMap
                ? <button type="button" style={S.linkBtn} onClick={() => setShowAllOnMap(true)}>⛶ Усі поля</button>
                : <button type="button" style={S.linkBtn} onClick={() => setShowAllOnMap(false)}>◉ Лише вибране</button>}
            </div>
          </div>

          <div style={S.mapGrid} className="dhMapGrid">
            <div style={S.mapWrap} className="dhMapWrap">
              {mode === MODES.RISK ? (
                <DashboardMapRiskView
                  height={600} token={token} fields={enrichedFields}
                  selectedId={selectedFieldId} showAll={showAllOnMap}
                  onSelect={(id) => { setSelectedFieldId(String(id)); setShowAllOnMap(false); }}
                  onOpenField={(id) => navigate(`/fields/${id}`)}
                />
              ) : (
                <DashboardMapView
                  height={600} token={token} mode={mode} fields={enrichedFields}
                  selectedId={selectedFieldId} showAll={showAllOnMap}
                  onSelect={(id) => { setSelectedFieldId(String(id)); setShowAllOnMap(false); }}
                  onOpenField={(id) => navigate(`/fields/${id}`)}
                />
              )}
            </div>

            <div style={S.listBox} className="dhListBox">
              {mode === MODES.RISK && (
                <div style={S.riskSummaryBar}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>Розподіл:</span>
                  {riskSummary.high   > 0 && <span style={S.chip("bad") }>🔴 {riskSummary.high}</span>}
                  {riskSummary.medium > 0 && <span style={S.chip("warn")}>🟠 {riskSummary.medium}</span>}
                  {riskSummary.low    > 0 && <span style={S.chip("ok")  }>🟢 {riskSummary.low}</span>}
                  {riskSummary.noData > 0 && <span style={S.chip()      }>⚪ {riskSummary.noData}</span>}
                </div>
              )}

              {mode === MODES.RISK && (
                <div style={S.riskEditor} className="dhRiskEditor">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={S.riskTitle}>🎨 Рівень ризику</div>
                    <div style={S.riskSub}>{selectedField ? selectedField.fieldName : "Виберіть поле"}</div>
                  </div>

                  <div style={S.riskBtnRow} className="dhRiskBtnRow">
                    {[
                      { tone: "default", lvl: "",       label: "⚪ Не обрано",  active: selectedField?.riskLevel === "NO_DATA" || !selectedField?.riskLevel },
                      { tone: "ok",      lvl: "LOW",    label: "🟢 Низький",    active: selectedField?.riskLevel === "LOW" },
                      { tone: "warn",    lvl: "MEDIUM", label: "🟠 Середній",   active: selectedField?.riskLevel === "MEDIUM" },
                      { tone: "bad",     lvl: "HIGH",   label: "🔴 Високий",    active: selectedField?.riskLevel === "HIGH" },
                    ].map(({ tone, lvl, label, active }) => (
                      <button key={label} type="button" style={S.riskBtn(tone, active)} className="dhRiskBtn"
                        disabled={!selectedField || savingRiskId === String(selectedFieldId)}
                        onClick={() => selectedField && setManualRisk(selectedField.id, lvl)}>
                        {label}
                      </button>
                    ))}
                  </div>

                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>Причина / коментар</div>
                    <textarea value={riskNoteInput} onChange={(e) => setRiskNoteInput(e.target.value)}
                      placeholder="Напр.: посуха, хвороба рослин…" maxLength={200} rows={2}
                      disabled={!selectedField} style={S.riskNoteField} />
                    {riskNoteById[selectedFieldId] && riskNoteById[selectedFieldId] !== riskNoteInput && (
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3 }}>Збережено: «{riskNoteById[selectedFieldId]}»</div>
                    )}
                  </div>

                  <div style={S.riskSub}>{savingRiskId ? "Зберігаю…" : selectedField ? "Натисни рівень щоб зберегти" : ""}</div>
                </div>
              )}

              <div style={S.fieldList} ref={listRef}>
                {listFields.map((f) => {
                  const active = String(f?.id) === String(selectedFieldId);
                  const leftColor =
                    mode === MODES.RISK
                      ? f.riskLevel === "HIGH" ? "#ef4444" : f.riskLevel === "MEDIUM" ? "#f59e0b" : f.riskLevel === "LOW" ? "#22c55e" : undefined
                      : f.overdueTasksCount > 0 ? "#f59e0b" : f.blockedTasksCount > 0 ? "#ef4444" : undefined;
                  const savedNote = riskNoteById[String(f.id)];

                  return (
                    <div key={String(f.id)} data-field-id={String(f.id)}
                      style={S.fieldItem(active, leftColor)}
                      onClick={() => { setSelectedFieldId(String(f.id)); setShowAllOnMap(false); }}
                      role="button" tabIndex={0} className="dhFieldItem">
                      <div style={{ flexShrink: 0 }}><FieldShapeIcon geometry={f.geometryJson} size={48} /></div>
                      <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                        <div style={S.fieldName}>{f.fieldName}</div>
                        <div style={S.chips}>
                          {mode !== MODES.RISK
                            ? <span style={S.chip("ok")}>{f.cropName ? `🌾 ${f.cropName}` : "🌾 —"}</span>
                            : <span style={S.chip(riskTone(f.riskLevel))}>⚠ {riskLabel(f.riskLevel)}</span>}
                          {f.overdueTasksCount > 0 && <span style={S.chip("warn")}>⏰ {f.overdueTasksCount}</span>}
                          {f.blockedTasksCount > 0 && <span style={S.chip("bad")}>⛔ {f.blockedTasksCount}</span>}
                          {f.activeTasksCount  > 0 && <span style={S.chip("ok") }>✅ {f.activeTasksCount}</span>}
                        </div>
                        {mode === MODES.RISK && savedNote && (
                          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📝 {savedNote}</div>
                        )}
                      </div>
                      <div style={S.fieldArea} className="dhFieldArea">{Number(f.areaHa || 0).toFixed(0)} га</div>
                    </div>
                  );
                })}
                {listFields.length === 0 && <div style={S.empty}>Немає полів для показу.</div>}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tasks + Crops ───────────────────────── */}
        <div style={S.sectionGrid} className="dhSectionGrid">

          <div style={S.sectionCard}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>✓ Задачі під контролем</div>
              <select value={taskPeriodDays} onChange={(e) => setTaskPeriodDays(Number(e.target.value))} style={S.select} className="dhControl">
                {TASK_PERIODS.map((p) => <option key={p.value} value={p.value}>Період: {p.label}</option>)}
              </select>
            </div>

            <div style={S.taskPanels} className="dhTaskPanels">
              <div style={S.taskPanel}>
                <div style={S.taskPanelHead}>
                  <div style={S.taskPanelTitle}>⏰ Прострочені</div>
                  <div style={S.taskCount("warn")}>{fmt(taskPanels.overdueRecent.length)}</div>
                </div>
                <div style={S.taskList}>
                  {taskPanels.overdueRecent.slice(0, 5).map((t) => (
                    <div key={t.id} style={S.taskRow("bad")}>
                      <div style={S.taskTitle}>{shortText(t.title, 52)}</div>
                      <div style={S.taskMetaRow}>
                        <span style={S.pill("bad")}>{t.deadlineMs != null ? `${Math.abs(t.daysDiff)} дн` : "без дати"}</span>
                        <span style={S.pill()}>{shortText(t.fieldName, 28)}</span>
                        <span style={S.pill("warn")}>до {formatTaskDate(t.deadline)}</span>
                      </div>
                    </div>
                  ))}
                  {taskPanels.overdueRecent.length === 0 && <div style={S.empty}>Немає прострочених.</div>}
                </div>
                <div style={S.taskFooter}>
                  <button type="button" style={S.linkBtn} onClick={() => openTasksWithPreset("overdue")}>Всі →</button>
                </div>
              </div>

              <div style={S.taskPanel}>
                <div style={S.taskPanelHead}>
                  <div style={S.taskPanelTitle}>✅ Активні</div>
                  <div style={S.taskCount("ok")}>{fmt(taskPanels.activeUpcoming.length)}</div>
                </div>
                <div style={S.taskList}>
                  {taskPanels.activeUpcoming.slice(0, 5).map((t) => (
                    <div key={t.id} style={S.taskRow(t.status === "IN_PROGRESS" ? "ok" : "warn")}>
                      <div style={S.taskTitle}>{shortText(t.title, 52)}</div>
                      <div style={S.taskMetaRow}>
                        <span style={S.pill("ok")}>{t.status === "IN_PROGRESS" ? "в роботі" : "заплановано"}</span>
                        <span style={S.pill()}>{shortText(t.fieldName, 28)}</span>
                        <span style={S.pill("warn")}>{t.deadline ? `до ${formatTaskDate(t.deadline)}` : "без дедлайну"}</span>
                      </div>
                    </div>
                  ))}
                  {taskPanels.activeUpcoming.length === 0 && <div style={S.empty}>Немає активних.</div>}
                </div>
                <div style={S.taskFooter}>
                  <button type="button" style={S.linkBtn} onClick={() => openTasksWithPreset("active")}>Всі →</button>
                </div>
              </div>
            </div>
          </div>

          <div style={S.sectionCard}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>🌱 Культури та зони</div>
              <select value={cropYearFilter} onChange={(e) => setCropYearFilter(e.target.value)} style={S.select} className="dhControl">
                <option value="ALL">Усі роки</option>
                {cropYears.map((y) => <option key={y} value={String(y)}>{y}</option>)}
              </select>
            </div>

            <div style={S.cropWrap}>
              <div style={S.cropStats} className="dhCropStats">
                <div style={S.miniStat}><div style={S.miniStatLabel}>Культур</div><div style={S.miniStatValue}>{fmt(yieldZoneCropRows.length)}</div></div>
                <div style={S.miniStat}><div style={S.miniStatLabel}>Площа зон</div><div style={S.miniStatValue}>{yieldZonesLoading ? "…" : `${fmt1(yieldZoneCropRows.reduce((s, r) => s + r.totalHa, 0))} га`}</div></div>
                <div style={S.miniStat}><div style={S.miniStatLabel}>Фільтр</div><div style={S.miniStatValue}>{cropYearFilter === "ALL" ? "Усі" : cropYearFilter}</div></div>
              </div>

              <div style={S.cropList}>
                {yieldZoneCropRows.slice(0, 8).map((row) => (
                  <div key={`${row.catKey}-${row.cropName}`} style={S.cropRow} className="dhCropRow">
                    <div style={{ ...S.cropThumb, background: `${row.cat.color}18`, border: `1px solid ${row.cat.color}40` }}>
                      <span style={{ fontSize: 22 }}>{row.cat.icon}</span>
                    </div>
                    <div style={S.cropMain}>
                      <div style={S.cropHeadRow}>
                        <div style={S.cropName}>{row.cropName}</div>
                        <div style={S.cropSub}>{fmt1(row.share)}% площі</div>
                      </div>
                      <div style={S.progressTrack}>
                        <div style={{ ...S.progressFill(row.percent), background: `linear-gradient(90deg, ${row.cat.color}80, ${row.cat.color})` }} />
                      </div>
                      <div style={S.cropHeadRow}>
                        <div style={S.cropSub}>{row.cat.label} · {row.zones} зон · {row.fieldsCount} пол.</div>
                        <div style={S.cropSub}>{cropYearFilter === "ALL" ? "Усі роки" : cropYearFilter}</div>
                      </div>
                    </div>
                    <div style={S.cropYield} className="dhCropYield">
                      <div style={{ ...S.cropYieldValue, color: row.cat.color }}>{fmt1(row.totalHa)}</div>
                      <div style={S.cropYieldLabel}>га в зонах</div>
                    </div>
                  </div>
                ))}
                {!yieldZonesLoading && yieldZoneCropRows.length === 0 && <div style={S.empty}>Немає зон врожайності.</div>}
                {yieldZonesLoading && <div style={S.empty}>Завантажую…</div>}
              </div>
            </div>
          </div>
        </div>

        {/* ── Огляд: Клімат / Події / Врожайність ──── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }} className="dhSectionGrid">

          {/* Кліматичні зони */}
          <div style={S.sectionCard}>
            <div style={{ padding: "20px 20px 18px" }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>🌦</div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text)", marginBottom: 6 }}>Кліматичні зони</div>
              <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6, marginBottom: 16 }}>
                {climateZonesLoading ? "Завантаження…" : climateZoneSummary.total > 0
                  ? `Є кліматичні ризики на ${climateZoneSummary.fieldsAffected > 0 ? `${climateZoneSummary.fieldsAffected} полях` : "полях"}`
                  : "Кліматичні зони ще не додані"}
              </div>
              {climateZoneSummary.total > 0 && !climateZonesLoading && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
                  {climateZoneSummary.risk.HIGH   > 0 && <span style={{ ...S.chip("bad"),  fontSize: 10 }}>Висок.: {climateZoneSummary.risk.HIGH}</span>}
                  {climateZoneSummary.risk.MEDIUM > 0 && <span style={{ ...S.chip("warn"), fontSize: 10 }}>Серед.: {climateZoneSummary.risk.MEDIUM}</span>}
                  {climateZoneSummary.risk.LOW    > 0 && <span style={{ ...S.chip("ok"),   fontSize: 10 }}>Низьк.: {climateZoneSummary.risk.LOW}</span>}
                </div>
              )}
              <button type="button" style={S.linkBtn} onClick={() => navigate("/fields")}>Переглянути →</button>
            </div>
          </div>

          {/* Проблемні події */}
          <div style={S.sectionCard}>
            <div style={{ padding: "20px 20px 18px" }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>⚠️</div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text)", marginBottom: 6 }}>Проблемні події</div>
              <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6, marginBottom: 16 }}>
                {fieldEventsLoading ? "Завантаження…" : eventSummary.total > 0
                  ? `Зафіксовано події на ${eventSummary.byFieldList.length > 0 ? `${eventSummary.byFieldList.length} полях` : "полях"}`
                  : "Проблемних подій не зафіксовано"}
              </div>
              {eventSummary.total > 0 && !fieldEventsLoading && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
                  {eventSummary.byType.filter(t => t.count > 0).map(t => (
                    <span key={t.key} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, fontWeight: 700, background: `${t.color}12`, border: `1px solid ${t.color}30`, color: t.color }}>
                      {t.icon} {t.label}
                    </span>
                  ))}
                </div>
              )}
              <button type="button" style={S.linkBtn} onClick={() => navigate("/fields")}>Переглянути →</button>
            </div>
          </div>

          {/* Врожайність */}
          <div style={S.sectionCard}>
            <div style={{ padding: "20px 20px 18px" }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>🌾</div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text)", marginBottom: 6 }}>Врожайність по полях</div>
              <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6, marginBottom: 16 }}>
                {yieldZonesLoading ? "Завантаження…" : yieldByYear.length > 0
                  ? `Зони врожайності по ${yieldByYear.length > 1 ? `${yieldByYear.length} рокам` : "полях"}`
                  : "Зони врожайності ще не заповнені"}
              </div>
              {yieldByYear.length > 0 && !yieldZonesLoading && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
                  {yieldByYear.slice(0, 3).map(r => (
                    <span key={r.year} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: "rgba(22,163,74,.10)", border: "1px solid rgba(22,163,74,.25)", color: "#15803d" }}>{r.year}</span>
                  ))}
                </div>
              )}
              <button type="button" style={S.linkBtn} onClick={() => navigate("/yield")}>Управління →</button>
            </div>
          </div>

        </div>

        {/* ── Поля з HIGH ризиком ──────────────────── */}
        {riskSummary.high > 0 && (
          <div style={{ ...S.sectionCard, marginBottom: 14 }}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>🔴 Поля з високим ризиком</div>
              <span style={{ ...S.chip("bad") }}>{fmt(riskSummary.high)} {riskSummary.high === 1 ? "поле" : riskSummary.high < 5 ? "поля" : "полів"}</span>
            </div>
            <div style={{ padding: "0 16px 14px" }}>
              {enrichedFields.filter(f => f.riskLevel === "HIGH").map((f, i) => (
                <div
                  key={f.id}
                  onClick={() => navigate(`/fields/${f.id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, background: i % 2 === 0 ? "var(--panel)" : "transparent", cursor: "pointer", marginBottom: 2 }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", flexShrink: 0 }} />
                  <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.fieldName}
                  </div>
                  {f.areaHa != null && <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>{fmt1(f.areaHa)} га</span>}
                  {f.cropName && <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>🌾 {f.cropName}</span>}
                  <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 700, whiteSpace: "nowrap" }}>Детальніше →</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DELETED_PLACEHOLDER -- Врожайність по роках */}
        <div style={{ ...S.sectionCard, marginBottom: 14 }}>
          <div style={S.cardHead}>
            <div style={S.cardTitle}>📈 Врожайність по роках</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {yieldZonesLoading
                ? <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700 }}>Завантаження…</span>
                : yieldActiveYear && <>
                    <span style={S.chip()}>Зон: {fmt(yieldActiveYear.zonesCount)}</span>
                    <span style={S.chip("ok")}>{fmt1(yieldActiveYear.totalHa)} га</span>
                    <span style={S.chip()}>Полів: {fmt(yieldActiveYear.fieldsCount)}</span>
                  </>
              }
              <button type="button" style={S.linkBtn} onClick={() => navigate("/yield")}>Управління →</button>
            </div>
          </div>
          <div style={{ padding: "0 16px 16px" }}>
            {!yieldZonesLoading && yieldByYear.length === 0 && (
              <div style={S.empty}>Немає зон врожайності.</div>
            )}
            {yieldByYear.length > 0 && (
              <>
                {/* Year tabs */}
                <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
                  {yieldByYear.map(r => {
                    const active = yieldActiveYear?.year === r.year;
                    return (
                      <button
                        key={r.year}
                        type="button"
                        onClick={() => setSelectedYieldYear(r.year)}
                        style={{
                          padding: "6px 16px", borderRadius: 20, fontWeight: 800, fontSize: 13,
                          cursor: "pointer", border: "1.5px solid",
                          borderColor: active ? "#16a34a" : "var(--border)",
                          background: active ? "rgba(22,163,74,.12)" : "var(--panel)",
                          color: active ? "#16a34a" : "var(--text)",
                          transition: ".15s",
                        }}
                      >
                        {r.year}
                        {r.year === new Date().getFullYear() && (
                          <span style={{ fontSize: 9, marginLeft: 4, opacity: .7 }}>●</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Per-field breakdown */}
                {yieldByField.length === 0
                  ? <div style={S.empty}>Немає зон для цього року.</div>
                  : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {yieldByField.map((f, i) => (
                        <div key={f.fieldId} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                          borderRadius: 10, background: i % 2 === 0 ? "var(--panel)" : "transparent",
                          border: "1px solid transparent",
                        }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {f.fieldName}
                          </div>
                          <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                            {f.zones} зон
                          </span>
                          <span style={S.chip("ok")}>{fmt1(f.totalHa)} га</span>
                          {f.topCat && (
                            <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                              {f.topCat.icon} {f.topCat.label}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                }
              </>
            )}
          </div>
        </div>

        {/* ── Кліматичні ризики + Проблемні події ──── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }} className="dhSectionGrid">

          {/* кліматичні зони */}
          <div style={S.sectionCard}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>🌦 Кліматичні ризики</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {climateZonesLoading
                  ? <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700 }}>Завантаження…</span>
                  : <>
                      <span style={S.chip()}>Зон: {fmt(climateZoneSummary.total)}</span>
                      <span style={S.chip("warn")}>Полів: {fmt(climateZoneSummary.fieldsAffected)}</span>
                      {climateZoneSummary.risk.HIGH > 0 && <span style={S.chip("bad")}>Висок.: {climateZoneSummary.risk.HIGH}</span>}
                    </>
                }
              </div>
            </div>
            <div style={{ padding: "0 16px 16px" }}>
              {!climateZonesLoading && climateZoneSummary.categories.length === 0 && (
                <div style={S.empty}>Немає кліматичних зон.</div>
              )}
              {climateZoneSummary.categories.length > 0 && (
                <div style={S.climaGrid}>
                  {climateZoneSummary.categories.map(cat => {
                    const total = cat.high + cat.medium + cat.low || 1;
                    return (
                      <div key={cat.catKey} style={S.climaCard(cat.color)}>
                        <div style={{ fontSize: 18, marginBottom: 4 }}>{cat.icon}</div>
                        <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text)", lineHeight: 1.2, marginBottom: 2 }}>{cat.label}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
                          {cat.zones} зон · {cat.fieldsCount} пол.
                        </div>
                        <div style={S.riskBar}>
                          {cat.high   > 0 && <div style={S.riskSegment("#ef4444", (cat.high   / total) * 100)} title={`Висок.: ${cat.high}`}   />}
                          {cat.medium > 0 && <div style={S.riskSegment("#f59e0b", (cat.medium / total) * 100)} title={`Серед.: ${cat.medium}`} />}
                          {cat.low    > 0 && <div style={S.riskSegment("#22c55e", (cat.low    / total) * 100)} title={`Низьк.: ${cat.low}`}    />}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>
                          {cat.high > 0 && <span style={{ color: "#ef4444", fontWeight: 700 }}>В:{cat.high} </span>}
                          {cat.medium > 0 && <span style={{ color: "#f59e0b", fontWeight: 700 }}>С:{cat.medium} </span>}
                          {cat.low > 0 && <span style={{ color: "#22c55e", fontWeight: 700 }}>Н:{cat.low}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* проблемні події */}
          <div style={S.sectionCard}>
            <div style={S.cardHead}>
              <div style={S.cardTitle}>⚠ Проблемні події</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {fieldEventsLoading
                  ? <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700 }}>Завантаження…</span>
                  : <>
                      <span style={S.chip()}>Подій: {fmt(eventSummary.total)}</span>
                      <span style={S.chip("warn")}>Полів: {fmt(eventSummary.byFieldList.length)}</span>
                    </>
                }
              </div>
            </div>
            <div style={{ padding: "0 16px 16px" }}>
              {!fieldEventsLoading && eventSummary.total === 0 && (
                <div style={S.empty}>Немає проблемних подій.</div>
              )}
              {eventSummary.total > 0 && (
                <>
                  <div style={S.evTypeRow}>
                    {eventSummary.byType.map(t => (
                      <div key={t.key} style={S.evTypeCard(t.color)}>
                        <div style={{ fontSize: 20 }}>{t.icon}</div>
                        <div style={{ fontWeight: 900, fontSize: 16, color: "var(--text)", lineHeight: 1, margin: "4px 0 2px" }}>{t.count}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)" }}>{t.label}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    {eventSummary.byFieldList.map((f, i) => (
                      <div key={f.fieldId} style={S.evFieldRow(i)}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {f.fieldName}
                        </div>
                        <span style={S.chip("bad")}>{f.events} под.</span>
                        <span style={{ ...S.chip(), fontSize: 11 }}>{f.points} точок</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>

        {/* ── Yield Zones ─────────────────────────── */}
        <div style={{ ...S.sectionCard, marginBottom: 14 }}>
          <div style={S.cardHead}>
            <div style={S.cardTitle}>🌾 Зони врожайності</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {yieldZonesLoading ? (
                <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700 }}>Завантаження…</span>
              ) : (
                <>
                  <span style={S.chip()}>Зон: {fmt(yieldZoneStats.totalZones)}</span>
                  <span style={S.chip("ok")}>{fmt1(yieldZoneStats.totalHa)} га</span>
                  <button type="button" style={S.linkBtn} onClick={() => navigate("/yield")}>Управління →</button>
                </>
              )}
            </div>
          </div>
          <div style={{ padding: "12px 16px" }}>
            {!yieldZonesLoading && yieldZoneStats.categories.length === 0 ? (
              <div style={{ ...S.empty, textAlign: "center", padding: "20px 0" }}>
                Немає зон за {year} рік.{" "}
                <span style={{ color: "#f59e0b", cursor: "pointer", fontWeight: 900 }} onClick={() => navigate("/yield")}>Додати →</span>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
                {yieldZoneStats.categories.map(cat => (
                  <div key={cat.value} style={S.yieldCat(cat.color)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, flexShrink: 0, background: `${cat.color}18`, border: `1.5px solid ${cat.color}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>{cat.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text)" }}>{cat.label}</div>
                        <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, marginTop: 1 }}>{cat.zones} {cat.zones === 1 ? "зона" : cat.zones < 5 ? "зони" : "зон"} · {fmt1(cat.ha)} га</div>
                      </div>
                      <div style={{ fontWeight: 900, fontSize: 14, color: cat.color, flexShrink: 0 }}>{fmt1(cat.percent)}%</div>
                    </div>
                    <div style={{ height: 5, borderRadius: 999, background: "rgba(120,120,120,.10)", overflow: "hidden" }}>
                      <div style={{ width: `${Math.max(4, cat.percent)}%`, height: "100%", borderRadius: 999, background: cat.color, transition: "width .4s" }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ───────────────────────────────── */}
        <div style={S.footer} className="dhFooter">
          <span style={S.footerLabel}>Рік: {year}</span>
          {[
            { label: `🌿 Полів: ${fmt(summary.totalFields)}`,    tone: "default" },
            { label: `📏 ${fmt1(summary.totalArea)} га`,         tone: "default" },
            { label: `✅ Активні: ${fmt(summary.active)}`,       tone: "ok" },
            { label: `⏰ Прострочені: ${fmt(summary.overdue)}`,  tone: summary.overdue > 0 ? "warn" : "default" },
            { label: `⛔ Заблоковані: ${fmt(summary.blocked)}`,  tone: summary.blocked > 0 ? "bad"  : "default" },
            { label: `📊 ${summary.avgYield == null ? "—" : fmt2(summary.avgYield)}`, tone: "default" },
          ].map(({ label, tone }) => <span key={label} style={S.pill(tone)}>{label}</span>)}
          {kpi?.fieldKpiDto && (
            <span style={S.pill()}>{fmt(kpi.fieldKpiDto.countField)} полів · {fmt(kpi.fieldKpiDto.sumArea)} га</span>
          )}
        </div>

        <style>{`
          .dhKpiCard:hover { transform: translateY(-2px); box-shadow: var(--shadow); }
          .dhFieldItem:hover { opacity: .88; }

          @media (max-width: 1400px) { .dhKpiGrid { grid-template-columns: repeat(3, 1fr) !important; } }
          @media (max-width: 900px)  { .dhKpiGrid { grid-template-columns: repeat(3, 1fr) !important; } }
          @media (max-width: 600px)  { .dhKpiGrid { grid-template-columns: 1fr 1fr !important; } }
          @media (max-width: 420px)  { .dhKpiGrid { grid-template-columns: 1fr !important; } }

          @media (max-width: 1180px) {
            .dhMapGrid { grid-template-columns: 1fr !important; height: auto !important; }
            .dhMapWrap { min-height: 480px; border-right: none !important; border-bottom: 1px solid var(--border); }
            .dhListBox { min-height: 360px !important; max-height: 500px; }
          }

          @media (max-width: 1320px) { .dhSectionGrid { grid-template-columns: 1fr !important; } }

          @media (max-width: 900px) {
            .dhTaskPanels { grid-template-columns: 1fr !important; }
            .dhCropStats  { grid-template-columns: 1fr 1fr !important; }
            .dhFooter     { flex-direction: column; align-items: stretch !important; }
          }

          @media (max-width: 768px) {
            .dhHRow    { display: grid !important; grid-template-columns: 1fr 1fr !important; }
            .dhTitle   { grid-column: 1 / -1; }
            .dhMeta    { grid-column: 1 / -1; margin-left: 0 !important; }
            .dhControl { width: 100%; min-height: 44px; font-size: 16px; }
          }

          @media (max-width: 560px) {
            .dhHRow      { grid-template-columns: 1fr !important; }
            .dhMapWrap   { min-height: 320px !important; }
            .dhListBox   { min-height: 260px !important; }
            .dhFieldItem { flex-direction: column !important; align-items: stretch !important; }
            .dhFieldArea { align-self: flex-start !important; margin-left: 0 !important; }
            .dhRiskBtnRow { display: grid !important; grid-template-columns: 1fr 1fr !important; }
            .dhRiskBtn   { width: 100%; text-align: center; min-height: 44px; }
            .dhCropRow   { grid-template-columns: 48px minmax(0, 1fr) !important; }
            .dhCropYield { grid-column: 1 / -1; text-align: left !important; min-width: 0 !important; }
            .dhKpiCard   { padding: 12px !important; gap: 10px !important; }
          }

          @media (max-width: 420px) {
            .dhMapWrap    { min-height: 280px !important; }
            .dhRiskBtnRow { grid-template-columns: 1fr !important; }
            .dhCropRow    { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </AppShell>
  );
}
